import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { User, FileText, Receipt, Lock, Loader2, Truck, Users, ShieldCheck, Tag, Landmark, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { NotificationEmailsPanel } from '@/components/merchant/NotificationEmailsPanel';
import { api } from '@/api/client';
import { MerchantProfileTab } from '@/features/merchants/tabs/MerchantProfileTab';
import { MerchantKycTab } from '@/features/merchants/tabs/MerchantKycTab';
import { MerchantBillingTab } from '@/features/merchants/tabs/MerchantBillingTab';
import { MerchantBankingTab } from '@/features/merchants/tabs/MerchantBankingTab';
import { MerchantRatesTab } from './MerchantRatesTab';
import { MerchantCouriersSelfTab } from './MerchantCouriersSelfTab';
import { MerchantTeamTab } from './MerchantTeamTab';
import { MerchantRolesTab } from './MerchantRolesTab';
import { LabelSettingsPanel } from '@/features/labels/LabelSettingsPanel';

type TabId = 'profile' | 'kyc' | 'banking' | 'billing' | 'notifications' | 'password' | 'couriers' | 'rates' | 'team' | 'roles' | 'labels';

const VALID_TABS: TabId[] = ['profile', 'kyc', 'banking', 'billing', 'notifications', 'password', 'couriers', 'rates', 'team', 'roles', 'labels'];

export function MerchantSettingsPage() {
  const { user } = useAuth();
  const merchantId = user?.id ?? 0;
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') as TabId | null;
  const [tab, setTab] = useState<TabId>(initialTab && VALID_TABS.includes(initialTab) ? initialTab : 'profile');

  // The Profile tab needs the merchant's actual record — logo, brand name, mobile, commercial terms.
  // The auth store only carries id/email/companyName, so building the object from `user` left the tab
  // blank (most visibly: the uploaded logo never appeared). Same query key as MerchantLayout, so the
  // sidebar and this page share one fetch and refresh together after a save.
  const { data: me, isLoading: meLoading, isError: meError, refetch: refetchMe } = useQuery({
    queryKey: ['merchant', 'me', 'profile'],
    queryFn: async () => (await api.get('/merchants/me/profile')).data as any,
    enabled: !!merchantId && user?.kind === 'merchant',
  });

  if (!merchantId || user?.kind !== 'merchant') {
    return <div className="p-10 text-center text-slate-500">Sign in as a merchant to access settings.</div>;
  }

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500">Manage your profile, KYC, billing, couriers, team, roles and password.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden grid grid-cols-[240px_1fr] min-h-[500px]">
        <nav className="border-r border-slate-200 bg-slate-50 p-3 space-y-1">
          <NavItem active={tab === 'profile'}  icon={<User size={15} />}        label="Profile"          sub="Company name, brand, logo"   onClick={() => setTab('profile')} />
          <NavItem active={tab === 'kyc'}      icon={<FileText size={15} />}    label="KYC Details"      sub="PAN, GST, Aadhaar"           onClick={() => setTab('kyc')} />
          <NavItem active={tab === 'banking'}  icon={<Landmark size={15} />}    label="Bank Details"     sub="Where your COD is remitted"  onClick={() => setTab('banking')} />
          <NavItem active={tab === 'billing'}  icon={<Receipt size={15} />}     label="Billing Details"  sub="Your GST invoicing address"  onClick={() => setTab('billing')} />
          <NavItem active={tab === 'notifications'} icon={<Bell size={15} />}   label="Notifications"    sub="Who gets which email"        onClick={() => setTab('notifications')} />
          <NavItem active={tab === 'couriers'} icon={<Truck size={15} />}       label="Couriers"         sub="Assigned couriers + priority" onClick={() => setTab('couriers')} />
          <NavItem active={tab === 'rates'}    icon={<Receipt size={15} />}     label="Courier Rates"    sub="What each courier costs you"  onClick={() => setTab('rates')} />
          <NavItem active={tab === 'labels'}   icon={<Tag size={15} />}         label="Label Settings"   sub="Logo, size, template on labels" onClick={() => setTab('labels')} />
          <NavItem active={tab === 'team'}     icon={<Users size={15} />}       label="Team"             sub="Your sub-users (accounts, ops)" onClick={() => setTab('team')} />
          <NavItem active={tab === 'roles'}    icon={<ShieldCheck size={15} />} label="Roles & Permissions" sub="Group permissions into roles" onClick={() => setTab('roles')} />
          <NavItem active={tab === 'password'} icon={<Lock size={15} />}        label="Change Password"  sub="Update your login password"  onClick={() => setTab('password')} />
        </nav>
        <div className="p-6 overflow-y-auto">
          {tab === 'profile'  && (
            me ? <MerchantProfileTab merchant={me} merchantView />
            : meLoading ? <div className="flex items-center gap-2 text-sm text-slate-500 py-10 justify-center">
                <Loader2 size={14} className="animate-spin" /> Loading your profile…
              </div>
            : <div className="py-10 text-center text-sm text-slate-500">
                {meError ? 'Could not load your profile.' : 'Profile unavailable.'}{' '}
                <button onClick={() => refetchMe()} className="text-primary underline">Retry</button>
              </div>
          )}
          {tab === 'kyc'      && <MerchantKycTab     merchantId={merchantId} merchantView />}
          {tab === 'banking'  && <MerchantBankingTab merchantId={merchantId} merchantView />}
          {tab === 'billing'  && <MerchantBillingTab merchantId={merchantId} merchantView />}
          {/* No merchantId → reads/writes the merchant's OWN notification routing. */}
          {tab === 'notifications' && <NotificationEmailsPanel />}
          {tab === 'couriers' && <MerchantCouriersSelfTab />}
          {tab === 'rates'    && <MerchantRatesTab merchantId={merchantId} />}
          {/* No merchantId → the panel reads/writes the merchant's OWN account (/merchants/me/profile),
              so a merchant can switch their label logo off without an admin doing it for them. */}
          {tab === 'labels'   && <LabelSettingsPanel embedded />}
          {tab === 'team'     && <MerchantTeamTab />}
          {tab === 'roles'    && <MerchantRolesTab />}
          {tab === 'password' && <ChangePasswordPanel />}
        </div>
      </div>
    </div>
  );
}

function NavItem({ active, icon, label, sub, onClick }: { active: boolean; icon: React.ReactNode; label: string; sub: string; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className={`w-full text-left px-3 py-2 rounded-lg transition flex items-start gap-2.5 ${active ? 'bg-white border border-slate-200 shadow-sm' : 'hover:bg-slate-100'}`}>
      <span className={`mt-0.5 ${active ? 'text-primary' : 'text-slate-500'}`}>{icon}</span>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${active ? 'text-primary' : 'text-slate-900'}`}>{label}</div>
        <div className="text-xs text-slate-500 truncate">{sub}</div>
      </div>
    </button>
  );
}

function ChangePasswordPanel() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  // Null until loaded, then a timestamp or null = never changed since the account was created.
  const [lastChanged, setLastChanged] = useState<string | null | undefined>(undefined);

  const loadLastChanged = async () => {
    try {
      const r = await api.get<{ passwordChangedAt: string | null }>('/merchants/me/password-info');
      setLastChanged(r.data?.passwordChangedAt ?? null);
    } catch { setLastChanged(null); }
  };
  useEffect(() => { loadLastChanged(); }, []);

  const submit = async () => {
    if (!current || !next || !confirm) { toast.error('Please fill all fields'); return; }
    if (next !== confirm) { toast.error('New password and confirm do not match'); return; }
    if (next.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setBusy(true);
    try {
      const r = await api.post('/merchants/me/change-password', { currentPassword: current, newPassword: next });
      toast.success('Password updated');
      setCurrent(''); setNext(''); setConfirm('');
      // Reflect the new "last changed" immediately from the response, then re-read to be sure.
      if ((r.data as any)?.passwordChangedAt) setLastChanged((r.data as any).passwordChangedAt);
      loadLastChanged();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Failed to update password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md">
      <h3 className="text-lg font-semibold text-slate-900 mb-1">Change your password</h3>
      {/* "Never" is the honest answer for an account still on the password it was created with —
          it's the case worth acting on, so it's shown in amber rather than hidden. */}
      <div className="text-xs mb-4">
        {lastChanged === undefined ? (
          <span className="text-slate-400">Checking last password change…</span>
        ) : lastChanged ? (
          <span className="text-slate-500">
            Password last changed: <span className="font-medium text-slate-700">
              {new Date(lastChanged).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
            </span>
          </span>
        ) : (
          <span className="text-amber-600">Password last changed: <span className="font-medium">Never</span></span>
        )}
      </div>
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Current password</label>
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:border-primary" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">New password</label>
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:border-primary" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Confirm new password</label>
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()} className="w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:border-primary" />
        </div>
        <button onClick={submit} disabled={busy} className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-60 flex items-center gap-2">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
          Update password
        </button>
      </div>
    </div>
  );
}
